import { z } from "zod";
import { recurringScheduleSchema } from "../scheduling/recurringSchedule.js";
const MAX_GURU_COLLECTIONS = 5;
const MAX_GURU_TAGS_PER_COLLECTION = 20;
const MAX_GURU_TAG_LENGTH = 100;
function isSalesforceIntegrationSettings(settings) {
  return "instance_url" in settings && (settings.instance_url === null || typeof settings.instance_url === "string") && "is_enabled" in settings && typeof settings.is_enabled === "boolean";
}
function isSlackIntegrationSettings(settings) {
  return "channel_id" in settings && (settings.channel_id === null || typeof settings.channel_id === "string");
}
const teamsIntegrationSettingsSchema = z.object({
  tenant_id: z.string().nullable()
});
function isTeamsIntegrationSettings(settings) {
  return teamsIntegrationSettingsSchema.safeParse(settings).success;
}
function isInternalTwilioSubAccountIntegrationSettings(provider, settings) {
  return provider === IntegrationProvider.INTERNAL_TWILIO_SUB_ACCOUNT && "account_sid" in settings;
}
function isExternalTwilioSubAccountIntegrationSettings(provider, settings) {
  return provider === IntegrationProvider.EXTERNAL_TWILIO_SUB_ACCOUNT && "account_sid" in settings;
}
const ExternalTwilioPartner = {
  FOUNTAIN: "fountain"
};
function isTwilioSubAccountIntegrationSettings(provider, settings) {
  return isInternalTwilioSubAccountIntegrationSettings(provider, settings) || isExternalTwilioSubAccountIntegrationSettings(provider, settings);
}
function isGreenhouseIntegrationSettings(settings) {
  return "advancement_stage_name" in settings && (settings.advancement_stage_name === null || typeof settings.advancement_stage_name === "string") && "integration_system_user_email" in settings && (settings.integration_system_user_email === null || typeof settings.integration_system_user_email === "string");
}
function isRingCentralRecordingS3IntakeIntegrationSettings(settings) {
  return "client_id" in settings && "enc_client_secret" in settings && "enc_jwt" in settings;
}
const recordingS3IntakeProviders = [
  "five9_recording_s3_intake",
  "ringcentral_recording_s3_intake"
];
function isRecordingS3IntakeProvider(provider) {
  return recordingS3IntakeProviders.includes(provider);
}
function getRecordingS3IntakeSettings(integration) {
  if (!isRecordingS3IntakeProvider(integration.provider)) {
    return null;
  }
  return integration.settings;
}
function getRingCxRecordingS3IntakeIdentifiers(integration) {
  if (integration.provider !== IntegrationProvider.RINGCENTRAL_RECORDING_S3_INTAKE) {
    return null;
  }
  const settings = integration.settings;
  return {
    client_id: settings.client_id ?? "",
    ringcx_sub_account_id: settings.ringcx_sub_account_id ?? null
  };
}
const kustomerOrgNamePattern = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;
const kustomerOrgNameSchema = z.string().trim().min(1).regex(
  kustomerOrgNamePattern,
  "The org name must be the bare org slug (letters, digits, and hyphens only) \u2014 not a domain or URL."
);
const kustomerIntegrationSettingsSchema = z.object({
  // The `{orgName}` in `{orgName}.api.kustomerapp.com`.
  org_name: z.string(),
  // The encryption layer owns this opaque envelope.
  enc_api_key: z.string(),
  // The canonical sync targets: one Alpharun KB per id. Populated by the
  // connect flow (every listed KB) when nothing is stored; a save may narrow
  // or extend it, but the sync never runs without at least one id.
  knowledge_base_ids: z.array(z.string()),
  sync_schedule: recurringScheduleSchema.optional(),
  autosync_enabled: z.boolean().optional()
});
const kustomerIntegrationRequestSchema = z.strictObject({
  org_name: kustomerOrgNameSchema,
  api_key: z.string().trim().min(1).optional(),
  sync_schedule: recurringScheduleSchema.optional(),
  autosync_enabled: z.boolean().optional()
});
const kustomerIntegrationToolingRequestSchema = kustomerIntegrationRequestSchema.extend({
  knowledge_base_ids: z.array(z.string()).transform((ids) => [
    ...new Set(ids.map((id) => id.trim()).filter((id) => id.length))
  ]).refine((ids) => ids.length > 0, {
    message: "At least one knowledge base id is required."
  })
});
function isKustomerIntegrationSettings(settings) {
  return kustomerIntegrationSettingsSchema.safeParse(settings).success;
}
const guruStoredCollectionSchema = z.object({
  collection_id: z.string(),
  enc_collection_token: z.string(),
  knowledge_base_name: z.string(),
  tags: z.array(z.string()).optional(),
  only_verified: z.boolean().optional()
});
const guruCollectionIdRequestSchema = z.uuid().transform((id) => id.toLowerCase());
const guruRequestCollectionSchema = z.object({
  collection_id: guruCollectionIdRequestSchema,
  collection_token: z.string().trim().min(1).optional(),
  // Customer setup derives this from Guru after credentials are verified.
  knowledge_base_name: z.string().trim().min(1).optional(),
  tags: z.array(z.string().trim().min(1).max(MAX_GURU_TAG_LENGTH)).max(MAX_GURU_TAGS_PER_COLLECTION).default([]).transform((tags) => {
    const uniqueTags = /* @__PURE__ */ new Map();
    for (const tag of tags) {
      uniqueTags.set(tag.toLowerCase(), tag);
    }
    return [...uniqueTags.values()];
  }),
  only_verified: z.boolean().default(false)
});
function guruCollectionsSchema(collectionSchema) {
  return z.array(collectionSchema).min(1).max(MAX_GURU_COLLECTIONS).refine(
    (collections) => new Set(collections.map((collection) => collection.collection_id)).size === collections.length,
    { message: "Guru collection ids must be unique" }
  );
}
const guruIntegrationSettingsShapeSchema = z.object({
  collections: z.array(guruStoredCollectionSchema),
  sync_schedule: recurringScheduleSchema.optional(),
  autosync_enabled: z.boolean().optional()
});
const guruIntegrationSettingsSchema = guruIntegrationSettingsShapeSchema.extend({
  collections: guruCollectionsSchema(guruStoredCollectionSchema)
});
const guruIntegrationRequestSchema = z.object({
  collections: guruCollectionsSchema(guruRequestCollectionSchema),
  sync_schedule: recurringScheduleSchema.optional(),
  autosync_enabled: z.boolean().optional()
});
function isGuruIntegrationSettings(settings) {
  return guruIntegrationSettingsShapeSchema.safeParse(settings).success;
}
const GENESYS_REGIONS = [
  "mypurecloud.com",
  // us-east-1, Americas (US East)
  "use2.us-gov-pure.cloud",
  // us-east-2, Americas (US East 2, gov)
  "usw2.pure.cloud",
  // us-west-2, Americas (US West)
  "cac1.pure.cloud",
  // ca-central-1, Americas (Canada)
  "mxc1.pure.cloud",
  // mx-central-1, Americas (Mexico)
  "sae1.pure.cloud",
  // sa-east-1, South America (São Paulo)
  "mypurecloud.ie",
  // eu-west-1, EMEA (Dublin)
  "euw2.pure.cloud",
  // eu-west-2, EMEA (London)
  "mypurecloud.de",
  // eu-central-1, EMEA (Frankfurt)
  "euc2.pure.cloud",
  // eu-central-2, EMEA (Zurich)
  "edee1.eusc-pure.cloud",
  // eusc-de-east-1, EU Sovereign Cloud (Germany East)
  "mec1.pure.cloud",
  // me-central-1, Middle East (UAE)
  "aps1.pure.cloud",
  // ap-south-1, Asia Pacific (Mumbai)
  "apse1.pure.cloud",
  // ap-southeast-1, Asia Pacific (Singapore)
  "mypurecloud.com.au",
  // ap-southeast-2, Asia Pacific (Sydney)
  "mypurecloud.jp",
  // ap-northeast-1, Asia Pacific (Tokyo)
  "apne2.pure.cloud",
  // ap-northeast-2, Asia Pacific (Seoul)
  "apne3.pure.cloud"
  // ap-northeast-3, Asia Pacific (Osaka)
];
const genesysRegionSchema = z.enum(GENESYS_REGIONS);
const GENESYS_DEFAULT_REGION = "mypurecloud.com";
function isGenesysIntegrationSettings(settings) {
  return "client_id" in settings && "enc_client_secret" in settings && "region" in settings && "is_enabled" in settings && "last_conversation_end_at" in settings;
}
const genesysIntegrationRequestSchema = z.strictObject({
  client_id: z.string().trim().min(1).max(200),
  client_secret: z.string().trim().min(1).max(500),
  region: genesysRegionSchema
});
const zoomRateLimitsSchema = z.object({
  // Requests/second budget for the Medium-labeled recordings *list* endpoint.
  list_qps: z.number().positive().optional(),
  // Requests/second budget for the (likely Heavy-labeled) media *download*.
  download_qps: z.number().positive().optional(),
  // Daily request budget for downloads (Heavy daily cap, shared with
  // resource-intensive APIs); stops us before a daily 429.
  download_daily: z.number().positive().optional()
});
const zoomQueueIdSchema = z.string().trim().min(1);
const zoomQueueIdsSchema = z.array(zoomQueueIdSchema).nullish();
const MAX_ZOOM_QUEUE_IDS = 1e3;
function isZoomContactCenterIntegrationSettings(settings) {
  return "account_id" in settings && "client_id" in settings && "enc_client_secret" in settings && "is_enabled" in settings && "last_recording_end_at" in settings;
}
const NICE_CXONE_DEFAULT_AREA = "na1";
const NICE_CXONE_DEFAULT_SEA_REGION = "us-west-2";
function isNiceCxoneIntegrationSettings(settings) {
  return "access_key_id" in settings && "enc_access_key_secret" in settings && "sea_bucket" in settings && "enc_sea_aws_secret_access_key" in settings && "agent_roster" in settings && "is_enabled" in settings;
}
function isRingCentralRingExIntegrationSettings(settings) {
  return "client_id" in settings && "enc_client_secret" in settings && "enc_jwt" in settings && "agents" in settings && "is_enabled" in settings && "last_discovery_end_at" in settings;
}
const zoomContactCenterIntegrationRequestSchema = z.strictObject({
  account_id: z.string().trim().min(1).max(200),
  client_id: z.string().trim().min(1).max(200),
  client_secret: z.string().trim().min(1).max(500)
});
const zoomQueueIdsRequestSchema = z.array(zoomQueueIdSchema.max(200)).max(MAX_ZOOM_QUEUE_IDS).nullable();
const IntegrationProvider = {
  FOUNTAIN: "fountain",
  INTERNAL_TWILIO_SUB_ACCOUNT: "internal_twilio_sub_account",
  EXTERNAL_TWILIO_SUB_ACCOUNT: "external_twilio_sub_account",
  SALESFORCE: "salesforce",
  SLACK: "slack",
  TEAMS: "teams",
  GREENHOUSE: "greenhouse",
  FIVE9_RECORDING_S3_INTAKE: "five9_recording_s3_intake",
  RINGCENTRAL_RECORDING_S3_INTAKE: "ringcentral_recording_s3_intake",
  KUSTOMER: "kustomer",
  GURU: "guru",
  GENESYS: "genesys",
  ZOOM_CONTACT_CENTER: "zoom_contact_center",
  API_RECORDING_UPLOAD: "api_recording_upload",
  NICE_CXONE: "nice_cxone",
  RINGCENTRAL_RINGEX: "ringcentral_ringex"
};
const allIntegrationProviders = [
  IntegrationProvider.FOUNTAIN,
  IntegrationProvider.INTERNAL_TWILIO_SUB_ACCOUNT,
  IntegrationProvider.EXTERNAL_TWILIO_SUB_ACCOUNT,
  IntegrationProvider.SALESFORCE,
  IntegrationProvider.SLACK,
  IntegrationProvider.TEAMS,
  IntegrationProvider.GREENHOUSE,
  IntegrationProvider.FIVE9_RECORDING_S3_INTAKE,
  IntegrationProvider.RINGCENTRAL_RECORDING_S3_INTAKE,
  IntegrationProvider.KUSTOMER,
  IntegrationProvider.GURU,
  IntegrationProvider.GENESYS,
  IntegrationProvider.ZOOM_CONTACT_CENTER,
  IntegrationProvider.API_RECORDING_UPLOAD,
  IntegrationProvider.NICE_CXONE,
  IntegrationProvider.RINGCENTRAL_RINGEX
];
const alpharunManagedIntegrationProviders = [
  ...recordingS3IntakeProviders,
  IntegrationProvider.API_RECORDING_UPLOAD,
  IntegrationProvider.NICE_CXONE,
  IntegrationProvider.RINGCENTRAL_RINGEX
];
function isAlpharunManagedIntegrationProvider(provider) {
  return alpharunManagedIntegrationProviders.includes(
    provider
  );
}
const integrationProviderSchema = z.enum(allIntegrationProviders);
function isIntegrationProvider(value) {
  return allIntegrationProviders.includes(value);
}
const genesysIntegrationCredentialsRequestSchema = genesysIntegrationRequestSchema.extend({
  provider: z.literal(IntegrationProvider.GENESYS)
});
const zoomContactCenterIntegrationCredentialsRequestSchema = zoomContactCenterIntegrationRequestSchema.extend({
  provider: z.literal(IntegrationProvider.ZOOM_CONTACT_CENTER)
});
const integrationCredentialsRequestSchema = z.discriminatedUnion(
  "provider",
  [
    genesysIntegrationCredentialsRequestSchema,
    zoomContactCenterIntegrationCredentialsRequestSchema
  ]
);
const integrationConnectRequestSchema = z.discriminatedUnion(
  "provider",
  [
    genesysIntegrationCredentialsRequestSchema,
    zoomContactCenterIntegrationCredentialsRequestSchema.extend({
      zoom_queue_ids: zoomQueueIdsRequestSchema.optional()
    })
  ]
);
const genesysIntegrationUpdateSchema = z.union([
  genesysIntegrationRequestSchema,
  z.strictObject({ is_enabled: z.boolean() })
]);
const zoomContactCenterIntegrationUpdateSchema = z.union([
  zoomContactCenterIntegrationRequestSchema,
  z.strictObject({ is_enabled: z.boolean() }),
  z.strictObject({ zoom_queue_ids: zoomQueueIdsRequestSchema })
]);
export {
  ExternalTwilioPartner,
  GENESYS_DEFAULT_REGION,
  GENESYS_REGIONS,
  IntegrationProvider,
  MAX_GURU_COLLECTIONS,
  MAX_GURU_TAGS_PER_COLLECTION,
  MAX_GURU_TAG_LENGTH,
  NICE_CXONE_DEFAULT_AREA,
  NICE_CXONE_DEFAULT_SEA_REGION,
  allIntegrationProviders,
  alpharunManagedIntegrationProviders,
  genesysIntegrationCredentialsRequestSchema,
  genesysIntegrationRequestSchema,
  genesysIntegrationUpdateSchema,
  genesysRegionSchema,
  getRecordingS3IntakeSettings,
  getRingCxRecordingS3IntakeIdentifiers,
  guruCollectionIdRequestSchema,
  guruIntegrationRequestSchema,
  guruIntegrationSettingsSchema,
  integrationConnectRequestSchema,
  integrationCredentialsRequestSchema,
  isAlpharunManagedIntegrationProvider,
  isExternalTwilioSubAccountIntegrationSettings,
  isGenesysIntegrationSettings,
  isGreenhouseIntegrationSettings,
  isGuruIntegrationSettings,
  isIntegrationProvider,
  isInternalTwilioSubAccountIntegrationSettings,
  isKustomerIntegrationSettings,
  isNiceCxoneIntegrationSettings,
  isRecordingS3IntakeProvider,
  isRingCentralRecordingS3IntakeIntegrationSettings,
  isRingCentralRingExIntegrationSettings,
  isSalesforceIntegrationSettings,
  isSlackIntegrationSettings,
  isTeamsIntegrationSettings,
  isTwilioSubAccountIntegrationSettings,
  isZoomContactCenterIntegrationSettings,
  kustomerIntegrationRequestSchema,
  kustomerIntegrationSettingsSchema,
  kustomerIntegrationToolingRequestSchema,
  kustomerOrgNameSchema,
  recordingS3IntakeProviders,
  zoomContactCenterIntegrationCredentialsRequestSchema,
  zoomContactCenterIntegrationRequestSchema,
  zoomContactCenterIntegrationUpdateSchema,
  zoomQueueIdsSchema,
  zoomRateLimitsSchema
};
//# sourceMappingURL=integration.js.map
